import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/** Letters (accents included), digits, space, . - & and the four engraving symbols */
const DISALLOWED = /[^\p{L}\p{N} .\-&♥∞★☾]/gu;
const FONT_NAMES = { classique: 'Classique', script: 'Script', baton: 'Bâton' };
/** Starting sizes in photo pixels, per font, for a similar visual height (inside band / top plate) */
const INSIDE_SIZE = { classique: 58, script: 64, baton: 46 };
/** Share of the plate face the top engraving may fill (text; the logo has its own slider) */
const TOP_TEXT_SCALE = 0.85;
const FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500&family=Great+Vibes&family=Montserrat:wght@600&display=swap';
const FONT_FACES = ['500 40px "Cormorant Garamond"', '40px "Great Vibes"', '600 40px Montserrat'];
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];
const LOGO_MAX_BYTES = 5 * 1024 * 1024;

/**
 * @typedef {object} Refs
 * @property {HTMLInputElement} [insideInput]
 * @property {HTMLButtonElement} [insideToggle]
 * @property {HTMLElement} [insidePanel]
 * @property {HTMLElement} [insideCounter]
 * @property {HTMLElement} [insideError]
 * @property {SVGSVGElement} [insideSvg]
 * @property {SVGImageElement} [insidePhoto]
 * @property {SVGGElement} [insideEngraving]
 * @property {SVGTextElement} [insideText]
 * @property {SVGTextElement} [insideTextLight]
 * @property {SVGTextPathElement} [insideTextPath]
 * @property {SVGTextPathElement} [insideLightPath]
 * @property {HTMLInputElement} [topInput]
 * @property {HTMLElement} [topCounter]
 * @property {HTMLElement} [topError]
 * @property {HTMLElement} [topTextPanel]
 * @property {HTMLElement} [topLogoPanel]
 * @property {SVGSVGElement} [topSvg]
 * @property {SVGImageElement} [topPhoto]
 * @property {SVGGElement} [topEngraving]
 * @property {SVGTextElement} [topText]
 * @property {SVGTextElement} [topTextLight]
 * @property {SVGImageElement} [topLogo]
 * @property {HTMLInputElement} [logoInput]
 * @property {HTMLElement} [logoName]
 * @property {HTMLElement} [logoSizeField]
 * @property {HTMLInputElement} [logoSize]
 * @property {HTMLOutputElement} [logoSizeValue]
 * @property {HTMLElement} [logoError]
 * @property {HTMLButtonElement} [logoRemove]
 * @property {HTMLElement} [logoUpload]
 * @property {HTMLElement} [logoActions]
 * @property {HTMLElement} [logoFile]
 * @property {HTMLElement} [topEmpty]
 * @property {HTMLElement} consent
 * @property {HTMLInputElement} consentInput
 * @property {HTMLElement} status
 *
 * @extends {Component<Refs>}
 */
class VarmierEngraving extends Component {
  requiredRefs = ['consent', 'consentInput', 'status'];

  #inside = { open: false, text: '', font: 'classique' };
  #top = {
    mode: 'text',
    text: '',
    font: 'classique',
    /** @type {File | null} */ file: null,
    /** Trimmed drawing shown in the preview (blank / transparent margins removed) */
    url: '',
    width: 0,
    height: 0,
    /** Logo size, % of the plate face */
    scale: 85,
  };
  #variantPrice = 0;
  #variantAvailable = true;
  #abortController = new AbortController();
  /** @type {number | null} */
  #syncTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#variantPrice = Number(this.dataset.variantPrice) || 0;
    this.#variantAvailable = this.dataset.variantAvailable !== 'false';

    const { signal } = this.#abortController;
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal,
    });
    window.addEventListener('resize', () => this.#renderPreviews(), { signal, passive: true });

    // The top engraving is open by default: its photo and fonts load once the block nears the viewport
    if (this.refs.topSvg) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          loadPhoto(this.refs.topPhoto);
          this.#loadFonts();
        },
        { rootMargin: '300px' }
      );
      observer.observe(this.refs.topSvg);
      signal.addEventListener('abort', () => observer.disconnect());
    }
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#syncTimer !== null) clearTimeout(this.#syncTimer);
    if (this.#top.url) URL.revokeObjectURL(this.#top.url);
  }

  /* ---------- Public API (product form, sticky add-to-cart bar) ---------- */

  /** True when the cart request must carry engraving data */
  get hasEngraving() {
    return this.#insideActive || this.#topActive;
  }

  /** True while the customer is working on an engraving (the sticky bar sends them back here) */
  get isEngaged() {
    return this.#inside.open || this.#topActive;
  }

  /**
   * Checks the engraving before add to cart; shows the reason and brings the block into view when blocked.
   * @returns {boolean}
   */
  validate() {
    const message = this.#blockingMessage();
    this.#render();
    if (!message) return true;
    this.reveal();
    return false;
  }

  /** Scrolls the block into view and focuses the field that needs attention */
  reveal() {
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' });

    const target =
      this.#inside.open && !this.#inside.text.trim()
        ? this.refs.insideInput
        : this.hasEngraving && !this.refs.consentInput.checked
        ? this.refs.consentInput
        : this.refs.topInput ?? this.refs.insideToggle;
    target?.focus({ preventScroll: true });
  }

  /**
   * Sends the add-to-cart request for the ring and, with an inside engraving, the linked paid line.
   * Returns the JSON of the last /cart/add response, as the product form expects.
   * @param {FormData} formData - The product form data (id, quantity, sections…)
   * @returns {Promise<any>}
   */
  async addToCart(formData) {
    const ringId = Number(formData.get('id'));
    const quantity = Number(formData.get('quantity')) || 1;
    const sections = formData.getAll('sections').filter(Boolean).pop() ?? '';

    /** @type {Record<string, string>} */
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match?.[1] && typeof value === 'string' && value) properties[match[1]] = value;
    }

    if (this.#insideActive) {
      properties['Gravure intérieure'] = this.#inside.text.trim();
      properties['Police intérieure'] = FONT_NAMES[/** @type {keyof typeof FONT_NAMES} */ (this.#inside.font)];
    }
    if (this.#top.mode === 'text' && this.#top.text.trim()) {
      properties['Gravure dessus'] = this.#top.text.trim();
      properties['Police dessus'] = FONT_NAMES[/** @type {keyof typeof FONT_NAMES} */ (this.#top.font)];
    }
    if (this.#top.mode === 'logo' && this.#top.file) properties['Taille du motif'] = `${this.#top.scale} %`;
    if (this.hasEngraving) properties._photo_avant_envoi = 'oui';

    const link = this.#insideActive ? createLinkId() : null;
    if (link) properties._lien_gravure = link;

    const engravingLine = link
      ? {
          id: Number(this.dataset.engravingVariantId),
          quantity,
          properties: { Emplacement: 'Intérieur', _lien_gravure: link },
        }
      : null;

    const logo = this.#top.mode === 'logo' ? this.#top.file : null;

    if (logo) {
      // Files only travel with a single-line request: the ring first, then its engraving line
      const body = new FormData();
      for (const [key, value] of formData.entries()) {
        if (!key.startsWith('properties[')) body.append(key, value);
      }
      for (const [key, value] of Object.entries(properties)) body.append(`properties[${key}]`, value);
      body.append('properties[Blason / image dessus]', logo, logo.name);

      const ringResponse = await postCart(body);
      if (ringResponse.status || !engravingLine) return ringResponse;

      const engravingResponse = await postCart(JSON.stringify({ items: [engravingLine], sections }));
      if (engravingResponse.status && ringResponse.key) {
        // Never leave an engraved ring without its paid line
        await fetch(Theme.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: ringResponse.key, quantity: 0 }),
        });
      }
      return engravingResponse;
    }

    if (engravingLine) {
      // The cart lists the last line of a request first: the engraving line goes first so it sits under its ring
      return postCart(
        JSON.stringify({ items: [engravingLine, { id: ringId, quantity, properties }], sections })
      );
    }

    const body = new FormData();
    for (const [key, value] of formData.entries()) {
      if (!key.startsWith('properties[')) body.append(key, value);
    }
    for (const [key, value] of Object.entries(properties)) body.append(`properties[${key}]`, value);
    return postCart(body);
  }

  /* ---------- Declarative handlers ---------- */

  /** @param {Event} event */
  toggleInside(event) {
    event.preventDefault();
    this.#inside.open = !this.#inside.open;
    if (this.#inside.open) {
      loadPhoto(this.refs.insidePhoto);
      this.#loadFonts();
    }
    this.#animatePanel(this.#inside.open);
    this.#render();
  }

  /** @param {Event} event */
  selectTopMode(event) {
    const tab = /** @type {HTMLElement} */ (event.target).closest('[data-mode]');
    if (!(tab instanceof HTMLElement)) return;
    this.#top.mode = tab.dataset.mode === 'logo' ? 'logo' : 'text';

    for (const button of this.querySelectorAll('.vme-tab')) {
      const selected = button === tab;
      button.setAttribute('aria-selected', String(selected));
      button.setAttribute('tabindex', selected ? '0' : '-1');
    }
    if (this.refs.topTextPanel) this.refs.topTextPanel.hidden = this.#top.mode !== 'text';
    if (this.refs.topLogoPanel) this.refs.topLogoPanel.hidden = this.#top.mode !== 'logo';
    this.#render();
  }

  /** Arrow keys move between the two tabs */
  /** @param {KeyboardEvent} event */
  handleTabKey(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const tabs = [...this.querySelectorAll('.vme-tab')];
    const index = tabs.indexOf(/** @type {Element} */ (event.target));
    const next = /** @type {HTMLElement | undefined} */ (tabs[(index + 1) % tabs.length]);
    next?.focus();
    next?.click();
  }

  /** @param {Event} event */
  handleInput(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    this.#applyText(input, input.value);
  }

  /** @param {Event} event */
  insertSymbol(event) {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-symbol]');
    if (!(button instanceof HTMLElement)) return;
    const input = button.dataset.place === 'top' ? this.refs.topInput : this.refs.insideInput;
    if (!input) return;

    const max = Number(input.maxLength) || 0;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const next = input.value.slice(0, start) + (button.dataset.symbol ?? '') + input.value.slice(end);
    if (max && next.length > max) {
      input.focus();
      return;
    }
    input.value = next;
    input.focus();
    input.setSelectionRange(start + 1, start + 1);
    this.#applyText(input, next);
  }

  /** @param {Event} event */
  selectFont(event) {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-font]');
    if (!(button instanceof HTMLElement)) return;
    const place = button.dataset.place === 'top' ? this.#top : this.#inside;
    place.font = button.dataset.font ?? 'classique';

    for (const option of this.querySelectorAll(`.vme-font[data-place="${button.dataset.place}"]`)) {
      option.setAttribute('aria-checked', String(option === button));
    }
    this.#render();
  }

  /** @param {Event} event */
  handleLogo(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const file = input.files?.[0] ?? null;
    const { logoError } = this.refs;

    if (file && (!LOGO_TYPES.includes(file.type) || file.size > LOGO_MAX_BYTES)) {
      input.value = '';
      if (logoError) {
        logoError.textContent = this.dataset.msgLogo ?? '';
        logoError.hidden = false;
      }
      return;
    }
    if (logoError) logoError.hidden = true;
    this.#setLogo(file);
  }

  /** Logo size slider: live preview, stored with the order for the engraver */
  /** @param {Event} event */
  handleLogoSize(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    this.#top.scale = Math.min(90, Math.max(70, Number(input.value) || 85));
    if (this.refs.logoSizeValue) this.refs.logoSizeValue.textContent = `${this.#top.scale} %`;
    this.#renderPreviews();
  }

  /** « Changer »: opens the file picker again */
  changeLogo() {
    this.refs.logoInput?.click();
  }

  removeLogo() {
    if (this.refs.logoInput) this.refs.logoInput.value = '';
    this.#setLogo(null);
    this.refs.logoInput?.focus();
  }

  handleConsent() {
    this.#render();
  }

  /* ---------- State ---------- */

  get #insideActive() {
    return this.#inside.open && this.#inside.text.trim() !== '';
  }

  get #topActive() {
    return this.#top.mode === 'logo' ? Boolean(this.#top.file) : this.#top.text.trim() !== '';
  }

  /** @returns {string} The reason add to cart is blocked, or an empty string */
  #blockingMessage() {
    if (this.#inside.open && !this.#inside.text.trim()) return this.dataset.msgEmpty ?? '';
    if (this.hasEngraving && !this.refs.consentInput.checked) return this.dataset.msgConsent ?? '';
    return '';
  }

  /**
   * @param {HTMLInputElement} input
   * @param {string} raw
   */
  #applyText(input, raw) {
    const place = input.dataset.place === 'top' ? 'top' : 'inside';
    const error = place === 'top' ? this.refs.topError : this.refs.insideError;
    const clean = raw.replace(DISALLOWED, '');

    if (clean !== raw) {
      const caret = (input.selectionStart ?? raw.length) - (raw.length - clean.length);
      input.value = clean;
      input.setSelectionRange(Math.max(caret, 0), Math.max(caret, 0));
      if (error) {
        error.textContent = this.dataset.msgInvalid ?? '';
        error.hidden = false;
      }
    } else if (error) {
      error.hidden = true;
    }

    (place === 'top' ? this.#top : this.#inside).text = clean;
    this.#render();
  }

  /** @param {File | null} file */
  /** @param {File | null} file */
  async #setLogo(file) {
    if (this.#top.url) URL.revokeObjectURL(this.#top.url);
    this.#top.file = file;
    this.#top.url = '';
    if (this.refs.logoName) {
      this.refs.logoName.textContent = file ? file.name : 'PNG, JPG ou SVG · 5 Mo max.';
    }
    if (this.refs.logoUpload) this.refs.logoUpload.hidden = Boolean(file);
    if (this.refs.logoActions) this.refs.logoActions.hidden = !file;
    if (this.refs.logoFile) this.refs.logoFile.textContent = file ? file.name : '';
    if (this.refs.logoSizeField) this.refs.logoSizeField.hidden = !file;

    if (file) {
      const trimmed = await trimImage(file);
      // A newer file may have been chosen while this one was being read
      if (this.#top.file !== file) {
        URL.revokeObjectURL(trimmed.url);
        return;
      }
      Object.assign(this.#top, trimmed);
    }
    this.#render();
  }


  /* ---------- Rendering ---------- */

  #render() {
    const { insideInput, insideCounter, topInput, topCounter, consent, consentInput, status } = this.refs;
    if (insideInput && insideCounter) insideCounter.textContent = `${insideInput.value.length} / ${insideInput.maxLength}`;
    if (topInput && topCounter) topCounter.textContent = `${topInput.value.length} / ${topInput.maxLength}`;

    consent.hidden = !this.hasEngraving;
    if (!this.hasEngraving) consentInput.checked = false;

    const message = this.#blockingMessage();
    status.textContent = message;

    this.#renderPreviews();
    this.#syncProductForm(Boolean(message));
  }

  #renderPreviews() {
    const { insideText, insideTextLight, insideTextPath, insideLightPath, insideEngraving } = this.refs;

    if (insideText && insideTextLight && insideTextPath && insideLightPath && insideEngraving) {
      const text = this.#inside.text.trim();
      const shown = text || this.dataset.placeholder || '';
      insideTextPath.textContent = shown;
      insideLightPath.textContent = shown;
      for (const layer of [insideText, insideTextLight]) layer.dataset.font = this.#inside.font;
      insideEngraving.toggleAttribute('data-empty', !text);
      if (this.#inside.open) this.#fitInside();
    }

    const { topText, topTextLight, topLogo, topEngraving } = this.refs;
    if (topText && topTextLight && topLogo && topEngraving) {
      const imageMode = this.#top.mode === 'logo';
      const showLogo = imageMode && Boolean(this.#top.url);
      const text = imageMode ? '' : this.#top.text.trim();
      // The image tab never shows the typed text: without a file it shows its own placeholder (« Votre blason ici »)
      const placeholder = imageMode ? this.dataset.logoPlaceholder : this.dataset.placeholder;
      const shown = showLogo ? '' : text || placeholder || '';
      if (this.refs.topEmpty) this.refs.topEmpty.hidden = !(imageMode && !this.#top.file);
      topText.textContent = shown;
      topTextLight.textContent = shown;
      for (const layer of [topText, topTextLight]) layer.dataset.font = this.#top.font;
      topEngraving.toggleAttribute('data-empty', !showLogo && !text);
      if (showLogo) {
        topLogo.setAttribute('href', this.#top.url);
        this.#fitLogo(topLogo);
      }
      topLogo.setAttribute('display', showLogo ? 'inline' : 'none');
      if (!showLogo) this.#fitTop();
    }
  }

  /**
   * Inside text: starts at the font's size, then shrinks until it stays between the zone ends
   * (x min / x max) and every character box stays within the band (centre line ± half height).
   */
  #fitInside() {
    const { insideSvg, insideText, insideTextLight } = this.refs;
    const path = insideSvg?.querySelector('defs path');
    if (!insideSvg || !insideText || !insideTextLight || !(path instanceof SVGPathElement)) return;

    const xMin = Number(insideSvg.dataset.xMin) || 0;
    const xMax = Number(insideSvg.dataset.xMax) || insideSvg.viewBox.baseVal.width;
    const half = Number(insideSvg.dataset.bandHalf) || 70;
    const centreY = this.#centreLine(path);
    let size = INSIDE_SIZE[/** @type {keyof typeof INSIDE_SIZE} */ (this.#inside.font)] ?? 60;

    const apply = () => {
      for (const layer of [insideText, insideTextLight]) layer.style.fontSize = `${size}px`;
    };
    const fits = () => {
      const box = insideText.getBBox();
      if (box.x < xMin || box.x + box.width > xMax) return false;
      for (let i = 0; i < insideText.getNumberOfChars(); i++) {
        const glyph = insideText.getExtentOfChar(i);
        for (const x of [glyph.x, glyph.x + glyph.width]) {
          const y = centreY(x);
          if (glyph.y < y - half || glyph.y + glyph.height > y + half) return false;
        }
      }
      return true;
    };

    apply();
    for (let step = 0; step < 24 && size > 14 && !fits(); step++) {
      size *= 0.93;
      apply();
    }
  }

  /**
   * The plate face on the top photo, from data-plate: « circle cx cy r » or « rect cx cy half corner ».
   * @returns {{ round: boolean, cx: number, cy: number, half: number, corner: number }}
   */
  #plate() {
    const [shape = 'circle', ...values] = (this.refs.topSvg?.dataset.plate ?? '').split(' ');
    const [cx = 512, cy = 512, half = 240, corner = 0] = values.map(Number);
    return { round: shape === 'circle', cx, cy, half, corner };
  }

  /**
   * Largest factor k so that a box of half-size (k·w, k·h), centred on the plate, stays inside the
   * safety zone (the plate face scaled by `share`): the corners must stay in the circle, or in the
   * rounded square (its rounded corners included).
   * @param {number} halfWidth
   * @param {number} halfHeight
   * @param {number} share - 0.7 to 0.9
   */
  #containFactor(halfWidth, halfHeight, share) {
    const plate = this.#plate();
    const half = plate.half * share;
    if (!halfWidth || !halfHeight) return 0;
    if (plate.round) return half / Math.hypot(halfWidth, halfHeight);

    const corner = plate.corner * share;
    const inside = (/** @type {number} */ k) => {
      const x = k * halfWidth;
      const y = k * halfHeight;
      if (x > half + 1e-6 || y > half + 1e-6) return false;
      const edge = half - corner;
      if (x <= edge || y <= edge) return true;
      return Math.hypot(x - edge, y - edge) <= corner + 1e-6;
    };
    let low = 0;
    let high = Math.min(half / halfWidth, half / halfHeight);
    if (inside(high)) return high;
    for (let step = 0; step < 40; step++) {
      const mid = (low + high) / 2;
      if (inside(mid)) low = mid;
      else high = mid;
    }
    return low;
  }

  /**
   * Logo: the trimmed drawing, scaled to « contain » in the safety zone chosen with the slider, centred.
   * @param {SVGImageElement} logo
   */
  #fitLogo(logo) {
    const { width, height } = this.#top;
    if (!width || !height) return;
    const plate = this.#plate();
    const k = this.#containFactor(width / 2, height / 2, this.#top.scale / 100);
    const w = width * k;
    const h = height * k;
    logo.setAttribute('x', String(plate.cx - w / 2));
    logo.setAttribute('y', String(plate.cy - h / 2));
    logo.setAttribute('width', String(w));
    logo.setAttribute('height', String(h));
  }

  /** Top text: as large as the safety zone allows (85 % of the plate face), centred */
  #fitTop() {
    const { topText, topTextLight } = this.refs;
    if (!topText || !topTextLight) return;
    const plate = this.#plate();
    for (const layer of [topText, topTextLight]) layer.setAttribute('x', String(plate.cx));

    let size = 200;
    const apply = () => {
      for (const layer of [topText, topTextLight]) layer.style.fontSize = `${size}px`;
    };
    apply();
    // The text box is not exactly centred on its anchor (glyph metrics): size on its farthest edges
    for (let pass = 0; pass < 3; pass++) {
      const box = topText.getBBox();
      if (!box.width || !box.height) return;
      const halfWidth = Math.max(plate.cx - box.x, box.x + box.width - plate.cx);
      const halfHeight = Math.max(plate.cy - box.y, box.y + box.height - plate.cy);
      const factor = this.#containFactor(halfWidth, halfHeight, TOP_TEXT_SCALE);
      size = Math.max(10, size * factor);
      apply();
    }
  }

  /** @type {((x: number) => number) | null} */
  #centreYAt = null;

  /**
   * Height of the text curve at a given x (the path runs left to right).
   * @param {SVGPathElement} path
   * @returns {(x: number) => number}
   */
  #centreLine(path) {
    if (this.#centreYAt) return this.#centreYAt;
    const total = path.getTotalLength();
    const points = Array.from({ length: 81 }, (_, i) => path.getPointAtLength((total * i) / 80));
    this.#centreYAt = (x) => {
      const first = points[0];
      const last = points[points.length - 1];
      if (!first || !last) return 0;
      if (x <= first.x) return first.y;
      if (x >= last.x) return last.y;
      const index = points.findIndex((point) => point.x >= x);
      const a = points[index - 1] ?? first;
      const b = points[index] ?? last;
      return a.y + ((b.y - a.y) * (x - a.x)) / (b.x - a.x || 1);
    };
    return this.#centreYAt;
  }

  /** @param {boolean} open */
  #animatePanel(open) {
    const { insidePanel, insideToggle } = this.refs;
    if (!insidePanel || !insideToggle) return;
    insideToggle.setAttribute('aria-expanded', String(open));
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (open) {
      insidePanel.hidden = false;
      if (reduced) return;
      const height = insidePanel.scrollHeight;
      insidePanel.style.height = '0px';
      requestAnimationFrame(() => {
        insidePanel.style.height = `${height}px`;
      });
      onTransitionEnd(insidePanel, () => {
        insidePanel.style.height = '';
      });
      return;
    }

    if (reduced) {
      insidePanel.hidden = true;
      return;
    }
    insidePanel.style.height = `${insidePanel.scrollHeight}px`;
    requestAnimationFrame(() => {
      insidePanel.style.height = '0px';
    });
    onTransitionEnd(insidePanel, () => {
      insidePanel.hidden = true;
      insidePanel.style.height = '';
    });
  }

  /* ---------- Product form: button state, total price ---------- */

  /** @param {boolean} blocked */
  #syncProductForm(blocked) {
    const section = this.closest('.shopify-section');
    const form = section?.querySelector('product-form-component');
    const engravingPrice = Number(this.dataset.engravingPrice) || 0;
    const total = this.#variantPrice + (this.#inside.open ? engravingPrice : 0);
    const money = (/** @type {number} */ cents) =>
      new Intl.NumberFormat('fr-FR', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(
        cents / 100
      );

    for (const container of form?.querySelectorAll('add-to-cart-component') ?? []) {
      const component = /** @type {any} */ (container);
      if (blocked) component.disable?.();
      else if (this.#variantAvailable) component.enable?.();

      const button = component.refs?.addToCartButton;
      if (!(button instanceof HTMLButtonElement)) continue;
      let suffix = button.querySelector('.vme-button-total');
      if (!suffix) {
        suffix = document.createElement('span');
        suffix.className = 'vme-button-total';
        button.querySelector('.add-to-cart__added')?.before(suffix) ?? button.append(suffix);
      }
      suffix.textContent = this.#inside.open ? ` · ${money(total)}` : '';
    }

    // The sticky bar sends the customer back here while an engraving is in progress
    const sticky = section?.querySelector('sticky-add-to-cart .sticky-add-to-cart__button');
    sticky?.toggleAttribute('aria-disabled', blocked);

    const price = section?.querySelector('.product-details product-price .price-with-suffix');
    if (price) {
      let line = price.querySelector('.vme-price-total');
      if (!line) {
        line = document.createElement('span');
        line.className = 'vme-price-total';
        price.append(line);
      }
      line.toggleAttribute('hidden', !this.#inside.open);
      line.innerHTML = this.#inside.open
        ? `${escapeHtml(this.dataset.totalLabel ?? '')} ${money(engravingPrice)} = <strong>${money(total)}</strong>`
        : '';
    }
  }

  /** @param {Event} event */
  #handleProductSelect = (event) => {
    const promise = /** @type {any} */ (event).promise;
    if (!promise) return;
    promise
      .then((/** @type {any} */ result) => {
        const variant = result?.detail?.resource;
        if (variant && typeof variant.price === 'number') {
          this.#variantPrice = variant.price;
          this.#variantAvailable = variant.available !== false;
        }
        // The product form updates its button after the same event: apply the engraving state after it
        if (this.#syncTimer !== null) clearTimeout(this.#syncTimer);
        this.#syncTimer = window.setTimeout(() => this.#render(), 150);
      })
      .catch(() => {});
  };

  #loadFonts() {
    if (!document.getElementById('varmier-engraving-fonts')) {
      const link = document.createElement('link');
      link.id = 'varmier-engraving-fonts';
      link.rel = 'stylesheet';
      link.href = FONTS_URL;
      document.head.append(link);
    }
    Promise.all(FONT_FACES.map((face) => document.fonts.load(face)))
      .then(() => this.#renderPreviews())
      .catch(() => {});
  }
}

/**
 * Crops the blank (transparent or near-white) margins of an uploaded logo, so the preview is sized
 * on the drawing itself. Returns a PNG object URL and the size of the drawing; falls back to the
 * original file when it cannot be read (e.g. an SVG without dimensions).
 * @param {File} file
 * @returns {Promise<{ url: string, width: number, height: number }>}
 */
async function trimImage(file) {
  const source = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = source;
    await image.decode();
    const scale = Math.min(1, 1600 / Math.max(image.naturalWidth || 1, image.naturalHeight || 1));
    const width = Math.max(1, Math.round((image.naturalWidth || 600) * scale));
    const height = Math.max(1, Math.round((image.naturalHeight || 600) * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('No canvas');
    context.drawImage(image, 0, 0, width, height);
    const { data } = context.getImageData(0, 0, width, height);

    let left = width;
    let top = height;
    let right = -1;
    let bottom = -1;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        const alpha = data[i + 3] ?? 0;
        const white = (data[i] ?? 0) > 240 && (data[i + 1] ?? 0) > 240 && (data[i + 2] ?? 0) > 240;
        if (alpha > 16 && !white) {
          if (x < left) left = x;
          if (x > right) right = x;
          if (y < top) top = y;
          if (y > bottom) bottom = y;
        }
      }
    }
    if (right < 0) throw new Error('Empty image');

    const cropWidth = right - left + 1;
    const cropHeight = bottom - top + 1;
    const crop = document.createElement('canvas');
    crop.width = cropWidth;
    crop.height = cropHeight;
    crop.getContext('2d')?.drawImage(canvas, left, top, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
    const blob = await new Promise((resolve) => crop.toBlob(resolve, 'image/png'));
    if (!(blob instanceof Blob)) throw new Error('No blob');
    URL.revokeObjectURL(source);
    return { url: URL.createObjectURL(blob), width: cropWidth, height: cropHeight };
  } catch {
    return { url: source, width: 600, height: 600 };
  }
}

/**
 * Sets the preview photo source on first use (photos are not requested with the product page).
 * @param {SVGImageElement | undefined} image
 */
function loadPhoto(image) {
  const source = image?.dataset.src;
  if (!image || !source || image.getAttribute('href')) return;
  image.setAttribute('href', source);
}

/**
 * @param {FormData | string} body - FormData (multipart) or a JSON string
 * @returns {Promise<any>}
 */
function postCart(body) {
  /** @type {Record<string, string>} */
  const headers = { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' };
  if (typeof body === 'string') headers['Content-Type'] = 'application/json';
  return fetch(Theme.routes.cart_add_url, { method: 'POST', headers, body }).then((response) => response.json());
}

/** @returns {string} A unique id linking a ring to its engraving line */
function createLinkId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @param {HTMLElement} element
 * @param {() => void} callback
 */
function onTransitionEnd(element, callback) {
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    callback();
  };
  element.addEventListener('transitionend', finish, { once: true });
  setTimeout(finish, 260);
}

/** @param {string} value */
function escapeHtml(value) {
  return value.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
}

if (!customElements.get('varmier-engraving')) {
  customElements.define('varmier-engraving', VarmierEngraving);
}
